import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Handyman = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my handyman business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps handyman businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for handyman businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most handyman businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my handyman business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like handyman. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Handyman - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Handyman CRM", url: "/industries/handyman" }
  ];

  const industryData = {
    name: 'Handyman Service',
    title: 'Stop Playing Phone Tag & Build a Base of Recurring Clients',
    subtitle: 'Capture service requests 24/7, automate follow-ups on estimates, and turn one-off jobs into maintenance contracts',
    icon: '🔧',
    challenges: [
      'Constant phone calls interrupting jobs',
      'Hard to follow up on all estimates',
      'One-time customers never call back',
      'Difficult to schedule efficiently',
      'Too much time spent on small jobs'
    ],
    solutions: [
      'Online booking captures calls 24/7',
      'Automated estimate follow-up',
      'Honey-do list subscription service',
      'Smart scheduling by location',
      'Minimum job value automation'
    ],
    features: [
      'Client CRM with job history',
      'Online booking system',
      'Automated estimate follow-ups',
      'Subscription service management',
      'Route optimization',
      'Photo documentation',
      'Review automation',
      'Senior/veteran discount tracking'
    ],
    results: [
      '60% convert to monthly subscriptions',
      'Book 50% more jobs',
      'Increase average job value by 35%',
      '5.9K average monthly revenue increase'
    ],
    reviews: [
      {
        author: 'Mike Patterson',
        location: 'Leeds',
        text: 'The emergency call capture system has been a game-changer. I used to miss 3-4 urgent calls a day because I\'m under sinks. Now every call is captured and I text them back within minutes. Booked 8 emergency jobs last week alone.',
        result: '8 emergency jobs in one week'
      },
      {
        author: 'Sarah Thompson',
        location: 'Manchester',
        text: 'The automated maintenance reminders have transformed my business. Customers actually book their annual boiler service without me chasing them. Last winter I had 127 boiler service bookings - all from automated reminders.',
        result: '127 boiler services from automation'
      },
      {
        author: 'James Wilson',
        location: 'Birmingham',
        text: 'Before this system, I was doing 2-3 emergency jobs a week. Now I\'m fully booked with emergency and regular work. The instant quote form means customers get pricing immediately and I\'m not playing phone tag all day.',
        result: 'Fully booked 3 weeks in advance'
      },
      {
        author: 'David Kumar',
        location: 'Bristol',
        text: 'The review automation got me from 12 Google reviews to 87 in 6 months. Now I show up first in Google Maps for "emergency plumber Bristol" and my phone doesn\'t stop ringing. Best investment I\'ve made.',
        result: '87 Google reviews, #1 in local search'
      },
      {
        author: 'Rachel Morgan',
        location: 'Liverpool',
        text: 'I was skeptical about online booking for a plumber, but customers love it. They can book drain cleaning or tap repairs without calling me. I fill my schedule in advance now instead of scrambling for work each morning.',
        result: 'Schedule filled 2 weeks ahead'
      },
      {
        author: 'Tom Henderson',
        location: 'Glasgow',
        text: 'The missed call text-back feature alone has paid for this system 10 times over. 62% of people who get my instant text back end up booking with me. That\'s jobs I would have lost to competitors before.',
        result: '62% of missed calls convert to bookings'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Handyman UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for handyman businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for handyman UK, handyman business software, handyman customer management, handyman lead generation UK"
        url="/industries/handyman"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Handyman;